import { FormContainer } from '@wyz/ui';
import { useI18n } from '~/common/context';
import { PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP } from '~/common/translators';
import { useExtraBonusEdition } from '~/purchase/hooks';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
import {
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicySeason,
  ISaveDistributionPricingPolicyBrandPayload,
} from '~/purchase/types';
/* eslint-disable @wyz/module/no-relative-import -- siblings are imported directly: going through
   the ~/purchase/components barrel would make this module depend on itself. */
import { ExtraBonusBrandRows } from './extra-bonus-brand-rows';
import { ExtraBonusTableHead } from './extra-bonus-table-head';
import './extra-bonus-table.scss';
/* eslint-enable @wyz/module/no-relative-import */

type Props = {
  brands: IDistributionPricingPolicyBrand[];
  seasons: IDistributionPricingPolicySeason[];
  canSeeExtraBonus: boolean;
  canEditExtraBonus?: boolean;
  isSaving?: boolean;
  onSaveBrand?: (
    payload: ISaveDistributionPricingPolicyBrandPayload,
  ) => Promise<unknown>;
  onEditAdvanced?: (brand: IDistributionPricingPolicyBrand) => void;
  printMode?: boolean;
};

/**
 * The grid holds one cell per brand, supplier type and season. When the diameters and vehicle
 * categories behind a cell disagree, the cell shows the range they span and the combinations are
 * moved to a tooltip, so a divergence stays visible without widening the table.
 *
 * The whole table is a single form: only the edited brand renders fields, and a `<form>` cannot sit
 * inside a `<tbody>`.
 */
export function ExtraBonusTable({
  brands,
  seasons,
  canSeeExtraBonus,
  canEditExtraBonus = false,
  isSaving = false,
  onSaveBrand,
  onEditAdvanced,
  printMode = false,
}: Props) {
  const { t } = useI18n();
  const {
    editedBrand,
    methods,
    draft,
    startEdition,
    cancelEdition,
    submitEdition,
  } = useExtraBonusEdition(onSaveBrand);

  const bonusTypeOptions = Object.values(PRICING_TYPE_ENUM).map((value) => ({
    value,
    label: t(PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP[value]),
  }));

  const metricCount = canSeeExtraBonus ? 3 : 1;

  return (
    <FormContainer
      methods={methods}
      submitHandler={submitEdition}
      className='extra-bonus-table__wrapper'
    >
      <table className='extra-bonus-table'>
        <ExtraBonusTableHead
          seasons={seasons}
          canSeeExtraBonus={canSeeExtraBonus}
          metricCount={metricCount}
        />

        <tbody>
          {brands.map((brand) => (
            <ExtraBonusBrandRows
              key={brand.brand}
              brand={brand}
              seasons={seasons}
              metricCount={metricCount}
              canSeeExtraBonus={canSeeExtraBonus}
              canEditExtraBonus={canEditExtraBonus}
              isEdited={editedBrand === brand.brand}
              isSaving={isSaving}
              draft={draft}
              bonusTypeOptions={bonusTypeOptions}
              onEdit={startEdition}
              onEditAdvanced={onEditAdvanced}
              onCancel={cancelEdition}
              printMode={printMode}
            />
          ))}
        </tbody>
      </table>
    </FormContainer>
  );
}
